import React from 'react';
import { Typography, Menu, MenuItem, Box, FormControlLabel } from '@mui/material';
import { Check, Sort } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SortDirection } from '../constants/SortDirection';
import { SortableProperty } from '../types/SortableProperty';
import { SortProperty } from '../types/SortProperty';
import { I18nKeys } from '../constants/I18nKeys';
import { PageHeaderButton } from './library/PageHeaderButton';

interface Props {
  readonly sortableProperties: SortableProperty[];
  readonly sortProperties: SortProperty[];
  onSort(sortProperties: SortProperty[]): void;
}

export const TableSortMenu: React.FC<Props> = ({ sortableProperties, sortProperties, onSort }: Props) => {
  const { t } = useTranslation();

  const buttonElRef = React.createRef<HTMLButtonElement>();
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const onSortClick = ({ sortKey, property }: SortableProperty, direction?: SortDirection): void => {
    setSortMenuAnchorEl(null);
    onSort(direction ? [{ key: sortKey || property, direction }] : []);
  };

  return (
    <Box>
      <PageHeaderButton
        ref={buttonElRef}
        id="header-sort-button"
        icon={<Sort />}
        label="Sort"
        onClick={() => setSortMenuAnchorEl(buttonElRef.current)}
      />
      <Menu
        id="header-sort-menu"
        anchorEl={sortMenuAnchorEl}
        open={Boolean(sortMenuAnchorEl)}
        onClose={() => setSortMenuAnchorEl(null)}
      >
        {sortableProperties.map((sortableProperty: SortableProperty) => {
          const key = sortableProperty.sortKey || sortableProperty.property;
          const found = sortProperties.find((sortProperty: SortProperty) => sortProperty.key === key);

          if (found) {
            return [
              <MenuItem
                disableGutters
                key={`${key}-ASC`}
                sx={{ py: '0px', pl: '7px', pr: '16px' }}
                onClick={() => {
                  onSortClick(
                    sortableProperty,
                    // if selected send undefined to remove the SortDirection on click
                    found.direction === SortDirection.Asc ? undefined : SortDirection.Asc,
                  );
                }}
              >
                <FormControlLabel
                  sx={{ mx: '0px' }}
                  control={
                    <Check
                      sx={{
                        m: '9px',
                        color: (theme) =>
                          found.direction === SortDirection.Asc ? theme.palette.secondary.main : 'transparent',
                      }}
                    />
                  }
                  label={
                    <Typography>{`${t(sortableProperty.i18nKey)} ${t(
                      I18nKeys.TableSortMenuAscendingText,
                    )}`}</Typography>
                  }
                />
              </MenuItem>,
              <MenuItem
                disableGutters
                key={`${key}-DESC`}
                sx={{ py: '0px', pl: '7px', pr: '16px' }}
                onClick={() => {
                  onSortClick(
                    sortableProperty,
                    // if selected send undefined to remove the SortDirection on click
                    found.direction === SortDirection.Desc ? undefined : SortDirection.Desc,
                  );
                }}
              >
                <FormControlLabel
                  sx={{ mx: '0px' }}
                  control={
                    <Check
                      sx={{
                        m: '9px',
                        color: (theme) =>
                          found.direction === SortDirection.Desc ? theme.palette.secondary.main : 'transparent',
                      }}
                    />
                  }
                  label={
                    <Typography>{`${t(sortableProperty.i18nKey)} ${t(
                      I18nKeys.TableSortMenuDescendingText,
                    )}`}</Typography>
                  }
                />
              </MenuItem>,
            ];
          }
          return (
            <MenuItem
              disableGutters
              key={key}
              sx={{ py: '0px', pl: '7px', pr: '16px' }}
              onClick={() => onSortClick(sortableProperty, SortDirection.Asc)}
            >
              <FormControlLabel
                sx={{ mx: '0px' }}
                control={
                  <Check
                    sx={{
                      m: '9px',
                      color: 'transparent',
                    }}
                  />
                }
                label={<Typography>{t(sortableProperty.i18nKey)}</Typography>}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
